import { z } from "zod";

export type FeatureFlagGroup = {
  key: string;
  title: string;
  description: string;
};

export const featureFlagGroups = [
  {
    key: "production",
    title: "Production Flags",
    description: "Flags that can be used for any tenant",
  },
  {
    key: "engineering",
    title: "Engineering Flags",
    description:
      "Flags that are risky, unstable, or have other caveats. Do not use these without talking to the engineering team first.",
  },
  {
    key: "experimental",
    title: "Experimental Flags",
    description:
      "Flags that are in the early stages of development and are not yet ready for general use",
  },
  {
    key: "hidden",
    title: "Hidden Flags",
    description:
      "Flags that are not displayed in the UI, only for internal testing",
  },
] as const;

export type FeatureFlagGroupKey = (typeof featureFlagGroups)[number]["key"];

export type FeatureFlagDefinition = {
  name: string;
  description: string;
  group: FeatureFlagGroupKey;
  type: z.ZodBoolean | z.ZodString | z.ZodNumber | z.ZodEnum<any>;
};

export const featureFlagDefinitions = {
  // Production flags
  addSubscribersWithoutSendingWelcomeMessage: {
    name: "Add Subscribers Without Sending Welcome Message",
    description:
      "Allow adding shortcode subscribers without sending the welcome message (for migrating existing subscribers)",
    group: "production",
    type: z.boolean(),
  },

  messagingAllowed: {
    name: "Messaging Allowed",
    description:
      "Allows sending messages; turn off to prevent sending or scheduling a campaign, sending replies, or currently sending campaigns",
    group: "production",
    type: z.boolean(),
  },

  skipVanPhoneNumberCheck: {
    name: "Skip NGP VAN phone number spot check",
    description:
      "If true, the phone number spot check we perform on VAN-associated contacts will be skipped",
    group: "production",
    type: z.boolean(),
  },

  skipInitialVanSyncs: {
    name: "Skip NGP VAN sync actions",
    description:
      "If true, NGP VAN sync actions will be marked SKIPPED instead of sent to VAN",
    group: "production",
    type: z.boolean(),
  },

  enableFakeMessagingStrategies: {
    name: "Enable fake messaging strategies",
    description:
      "If true, users can create fake numbers and use fake messaging strategies",
    group: "production",
    type: z.boolean(),
  },

  actionKitWebhooks: {
    name: "ActionKit Integration webhooks",
    description: "Enables webhooks for our ActionKit integration",
    group: "production",
    type: z.boolean(),
  },

  actionKitMultiPhoneNumberImport: {
    name: "Enable multiple phone numbers for a contact in ActionKit",
    description:
      "If true, allows users to input the names of multiple phone number columns for an ActionKit Import",
    group: "production",
    type: z.boolean(),
  },

  apiKeys: {
    name: "API Keys",
    description: "Enable API Key management",
    group: "production",
    type: z.boolean(),
  },
  sendCampaignApi: {
    name: "Enable send-campaign API endpoint",
    description:
      "Allow use of the send-campaign API. apiKeys must also be enabled. Customers must sign a contract addendum and be vetted by Engineering before using this API.",
    group: "production",
    type: z.boolean(),
  },

  // Engineering flags
  contactLimit: {
    name: "Max contacts per campaign / upload",
    description:
      "The maximum number of contacts that can be uploaded for campaigns (also limits upload size for suppressions)",
    group: "engineering",
    type: z.number(),
  },

  s3NonincrementalRowLimit: {
    name: "S3 Non-Incremental Sync Row Limit",
    description:
      "The maximum number of rows per table for the non-incremental mode of the S3 sync",
    group: "engineering",
    type: z.number(),
  },

  s3IncrementalWindowDays: {
    name: "S3 Incremental Sync Window Days",
    description:
      "The number of days of data to sync for the incremental mode of the S3 sync",
    group: "engineering",
    type: z.number(),
  },

  s3TableTimeoutMinutes: {
    name: "S3 Table Timeout",
    description:
      "The maximum number of minutes to allow each table export to run for the S3 sync",
    group: "engineering",
    type: z.number(),
  },

  overlapMaxContacts: {
    name: "Max contacts for campaign overlap calculation",
    description:
      "The maximum number of contacts to use for overlap calculations",
    group: "engineering",
    type: z.number(),
  },

  useJotformFor10dlcRegistration: {
    name: "Use Jotform for 10DLC Registrations",
    description:
      "Show a Jotform link for 10DLC registration instead of the Retool embedded form",
    group: "production",
    type: z.boolean(),
  },

  allowMessagingCanadianNumbers: {
    name: "Allow messaging Canadian numbers from U.S. tenants",
    description:
      "If true, Canadian region numbers will be treated as valid for U.S. tenants.",
    group: "production",
    type: z.boolean(),
  },

  // Experimental flags
  preferredLanguageSelection: {
    name: "Preferred Language Selection",
    description:
      "Enables the 'Preferred Language' dropdown on the user profile page",
    group: "experimental",
    type: z.boolean(),
  },
  mmsAttachmentUrlFromContact: {
    name: "MMS Attachment URL from Contact",
    description:
      "Enables the ability to use a url in a contact field as an MMS attachment",
    group: "experimental",
    type: z.boolean(),
  },
  personalizedGifs: {
    name: "Personalized GIFs",
    description: "Enables personalized GIFs",
    group: "experimental",
    type: z.boolean(),
  },
  defaultToDncOptOutFlow: {
    name: "DNC Opt Out Flow by default",
    description:
      "If true, newly created NGPVAN integrations will use the DNC opt out flow",
    group: "experimental",
    type: z.boolean(),
  },
  tMobileDailyCapBehavior: {
    name: "T-Mobile Daily Cap Exceeded behavior",
    description: `How to handle 'T-Mobile Daily Cap Exceeded' (4870) errors.
      autopause - 4870s are included in the standard campaign auto-pause calculation
      aggressive - Campaigns are auto-paused once they reach 100 4870 errors.
    `,
    group: "experimental",
    type: z.enum(["autopause", "aggressive"]),
  },
  tenDlcRegistrationInApp: {
    name: "10DLC Registration In-App",
    description:
      "If true, admins will be be shown the built-in 10DLC registration form instead of the Retool/Jotform form",
    group: "experimental",
    type: z.boolean(),
  },
  tollFreeRegistrationInApp: {
    name: "Toll-free Registration In-App",
    description:
      "If true, admins will be be shown the built-in toll-free registration form instead of the Jotform form",
    group: "experimental",
    type: z.boolean(),
  },
  unicodeStyleEditor: {
    name: "Unicode Style Editor",
    description: "Enables Unicode styles in the script editor",
    group: "experimental",
    type: z.boolean(),
  },
  budgets: {
    name: "Budgets",
    description: "Enables budgets",
    group: "experimental",
    type: z.boolean(),
  },
  generalInboxes: {
    name: "General Inboxes",
    description: "Enables general inboxes",
    group: "experimental",
    type: z.boolean(),
  },
  responseScriptImports: {
    name: "Response Script Importing",
    description: "Enables the response script import feature",
    group: "experimental",
    type: z.boolean(),
  },
  analyticsDashboardV2: {
    name: "Analytics Dashboard V2",
    description: "Enables analytics dashboard v2",
    group: "experimental",
    type: z.boolean(),
  },
  audienceCustomFields: {
    name: "Audience Custom Fields",
    description: "Enables custom fields for audiences",
    group: "experimental",
    type: z.boolean(),
  },
  donationHistoryPage: {
    name: "Donation History Page",
    description:
      "Enables the Donation History page (DEPRECATED - this flag is now set at the subaccount level)",
    group: "experimental",
    type: z.boolean(),
  },
  useDonationHistoryData: {
    name: "Use Donation History Data",
    description:
      "Use DonationHistory model instead of Donation/DonationRefund models",
    group: "experimental",
    type: z.boolean(),
  },
  tollFreeRegistrationEinRequired: {
    name: "Toll-Free Registration EIN Required",
    description: "Make EIN a required field on a toll-free registration",
    group: "experimental",
    type: z.boolean(),
  },
  tollFreeRegistrationCvTokenRequired: {
    name: "Toll-Free Registration CV Token Required",
    description: "Make CV token a required field on a toll-free registration",
    group: "experimental",
    type: z.boolean(),
  },
  cvTokenSubmittedToBandwidth: {
    name: "CV Token Submitted to Bandwidth",
    description:
      "Include CV token in a toll-free registration submission to Bandwidth",
    group: "experimental",
    type: z.boolean(),
  },
  tagGroups: {
    name: "Allow users to create single-select tags",
    description:
      "Enables the ability for users to create single-select tag groups in addition to flat tags",
    group: "experimental",
    type: z.boolean(),
  },
  enableApprovalsMode: {
    name: "Approvals Mode",
    description: "Enables approvals mode for campaigns",
    group: "experimental",
    type: z.boolean(),
  },
  longCodeForOptIn: {
    name: "Long Code for Opt-In",
    description: "Allows long code numbers to be used for opt-in texting",
    group: "experimental",
    type: z.boolean(),
  },
  enableMultiAudience: {
    name: "Allow users to have custom, non opt-in audiences",
    description:
      "When disabled, subaccounts are only allowed one (1) audience (opt-in only). When enabled, subaccounts are allowed to have multiple non opt-in audiences.",
    group: "experimental",
    type: z.boolean(),
  },

  // Test flags
  testBoolean: {
    name: "Test Boolean",
    description: "A test boolean flag",
    group: "hidden",
    type: z.boolean(),
  },
  testBooleanDefaultTrue: {
    name: "Test Boolean 2",
    description: "A test boolean flag with a default of true",
    group: "hidden",
    type: z.boolean(),
  },
  testString: {
    name: "Test String",
    description: "A test string flag",
    group: "hidden",
    type: z.string(),
  },
  testNumber: {
    name: "Test Number",
    description: "A test number flag",
    group: "hidden",
    type: z.number(),
  },
  testEnum: {
    name: "Test Enum",
    description: "A test enum flag",
    group: "hidden",
    type: z.enum(["a", "b", "c"]),
  },
} as const satisfies Record<string, FeatureFlagDefinition>;
